import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import {
  AuthResponse,
  AuthUser,
  ChangePasswordPayload,
  ChangePasswordResponse,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  LogoutResponse,
  ProfileResponse,
  ResetPasswordPayload,
  ResetPasswordResponse,
  UpdateProfilePayload,
  UpdateProfileResponse,
  ValidatePasswordResetResponse,
  UserType,
} from '@core/models/auth.model';
import { TokenStorageService } from '@core/storage/token-storage.service';
import { ApiService } from '../api.service';
import { PermissionService } from '../permission.service';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  name: string;
  email: string;
  phone?: string | null;
  nationality: string;
  password: string;
  password_confirmation: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly currentUserSubject = new BehaviorSubject<AuthUser | null>(null);

  readonly currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private readonly api: ApiService,
    private readonly tokenStorage: TokenStorageService,
    private readonly permissionService: PermissionService,
  ) {
    queueMicrotask(() => this.restoreSession());
  }

  login(payload: LoginPayload): Observable<AuthResponse> {
    return this.api.post<AuthResponse>('/auth/login', payload).pipe(
      tap((response) => this.handleAuthSuccess(response, 'customer')),
    );
  }

  adminLogin(payload: LoginPayload): Observable<AuthResponse> {
    return this.api.post<AuthResponse>('/admin/admin-login', payload).pipe(
      tap((response) => {
        this.handleAuthSuccess(response, 'admin');
        this.permissionService.removePermissions();
      }),
    );
  }

  register(payload: RegisterPayload): Observable<AuthResponse> {
    return this.api.post<AuthResponse>('/auth/register', payload).pipe(
      tap((response) => this.handleAuthSuccess(response, 'customer')),
    );
  }

  loadProfile(): Observable<ProfileResponse> {
    return this.api.get<ProfileResponse>('/auth/profile').pipe(
      tap((response) => {
        if (response.data?.user) {
          this.currentUserSubject.next(response.data.user);
        }
      }),
    );
  }

  ensureSessionFor(expectedType: UserType): Observable<boolean> {
    const user = this.getCurrentUser();

    if (user?.type === expectedType) {
      return of(true);
    }

    if (user && user.type !== expectedType) {
      return of(false);
    }

    if (!this.isAuthenticated()) {
      return of(false);
    }

    return this.loadProfile().pipe(
      map((response) => response.data?.user?.type === expectedType),
      catchError(() => of(false)),
    );
  }

  ensureDashboardSession(): Observable<boolean> {
    const user = this.getCurrentUser();

    if (user && this.canAccessDashboard()) {
      return of(true);
    }

    if (user && !this.canAccessDashboard()) {
      return of(false);
    }

    if (!this.isAuthenticated()) {
      return of(false);
    }

    return this.loadProfile().pipe(
      map((response) => {
        const loadedUser = response.data?.user;
        if (loadedUser) {
          this.currentUserSubject.next(loadedUser);
        }
        return this.canAccessDashboard();
      }),
      catchError(() => of(false)),
    );
  }

  changePassword(payload: ChangePasswordPayload): Observable<ChangePasswordResponse> {
    return this.api.post<ChangePasswordResponse>('/auth/change-password', payload);
  }

  forgotPassword(payload: ForgotPasswordPayload): Observable<ForgotPasswordResponse> {
    return this.api.post<ForgotPasswordResponse>('/auth/forgot-password', payload);
  }

  validatePasswordReset(email: string, token: string): Observable<ValidatePasswordResetResponse> {
    return this.api.get<ValidatePasswordResetResponse>('/auth/reset-password/validate', { email, token });
  }

  resetPassword(payload: ResetPasswordPayload): Observable<ResetPasswordResponse> {
    return this.api.post<ResetPasswordResponse>('/auth/reset-password', payload);
  }

  updateProfile(payload: UpdateProfilePayload): Observable<UpdateProfileResponse> {
    return this.api.put<UpdateProfileResponse>('/auth/profile', payload).pipe(
      tap((response) => {
        if (response.data?.user) {
          this.currentUserSubject.next(response.data.user);
        }
      }),
    );
  }

  logout(): Observable<LogoutResponse | null> {
    if (!this.tokenStorage.hasToken()) {
      this.clearSession();
      return of(null);
    }

    return this.api.post<LogoutResponse>('/auth/logout', {}).pipe(
      tap(() => this.clearSession()),
      catchError(() => {
        this.clearSession();
        return of(null);
      }),
    );
  }

  clearSession(): void {
    this.tokenStorage.removeToken();
    this.currentUserSubject.next(null);
    this.permissionService.removePermissions();
  }

  isAuthenticated(): boolean {
    return this.tokenStorage.hasToken();
  }

  isAdmin(): boolean {
    return this.getCurrentUser()?.type === 'admin';
  }

  isStaff(): boolean {
    return this.getCurrentUser()?.type === 'staff';
  }

  canAccessDashboard(): boolean {
    const type = this.getCurrentUser()?.type;
    return type === 'admin' || type === 'staff';
  }

  isCustomer(): boolean {
    return this.getCurrentUser()?.type === 'customer';
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  private restoreSession(): void {
    if (!this.tokenStorage.hasToken()) {
      return;
    }

    this.loadProfile().subscribe();
  }

  private handleAuthSuccess(response: AuthResponse, expectedType: UserType): void {
    const token = response.data?.token;
    const user = response.data?.user;

    if (token) {
      this.tokenStorage.setToken(token);
    }

    if (user) {
      this.currentUserSubject.next({
        ...user,
        type: user.type ?? expectedType,
      });
    }
  }
}
